'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface MaintenanceTask {
  frequency: string;
  tasks: string[];
  season?: string;
}

const MaintenanceSchedule = () => {
  const [activeTab, setActiveTab] = useState<'routine' | 'seasonal'>('routine');

  const routineTasks: MaintenanceTask[] = [
    {
      frequency: 'Weekly',
      tasks: [
        'Gently vacuum curtains using upholstery attachment',
        'Check for loose threads or minor damage',
        'Ensure proper draping and fold alignment',
        'Dust curtain rods and hardware'
      ]
    },
    {
      frequency: 'Monthly',
      tasks: [
        'Inspect lining for signs of sun damage',
        'Check and tighten hardware connections',
        'Steam wrinkles if necessary',
        'Rotate panels for even light exposure',
        'Clean window tracks and sills'
      ]
    },
    {
      frequency: 'Quarterly',
      tasks: [
        'Deep vacuum including pleats and folds',
        'Inspect for moth damage (natural fibers)',
        'Check cord and pulley systems',
        'Professional assessment for delicate fabrics',
        'Adjust tie-backs and holdbacks'
      ]
    },
    {
      frequency: 'Annually',
      tasks: [
        'Professional cleaning by certified specialists',
        'Complete hardware inspection and lubrication',
        'UV protection treatment application',
        'Lining replacement if needed',
        'Documentation of condition for insurance'
      ]
    }
  ];

  const seasonalTasks: MaintenanceTask[] = [
    {
      frequency: 'Spring',
      season: 'March - May',
      tasks: [
        'Deep cleaning after winter months',
        'Inspect for moisture damage from humidity',
        'Switch to lighter fabrics if applicable',
        'Check for pest activity in natural fibers',
        'Refresh moth protection treatments'
      ]
    },
    {
      frequency: 'Summer',
      season: 'June - August',
      tasks: [
        'Apply UV protection treatments',
        'Monitor for sun fading on exposed panels',
        'Ensure proper ventilation to prevent mildew',
        'Adjust for increased sunlight hours',
        'Consider blackout linings for heat control'
      ]
    },
    {
      frequency: 'Autumn',
      season: 'September - November',
      tasks: [
        'Prepare heavier fabrics for winter',
        'Inspect insulation properties of linings',
        'Check for draft prevention effectiveness',
        'Clean thoroughly before heating season',
        'Store summer curtains properly'
      ]
    },
    {
      frequency: 'Winter',
      season: 'December - February',
      tasks: [
        'Monitor condensation on windows',
        'Check for moisture damage from heating',
        'Ensure proper insulation performance',
        'Maintain consistent indoor humidity',
        'Plan spring professional cleaning'
      ]
    }
  ];

  return (
    <section data-component-id="src/app/care-maintenance/components/MaintenanceSchedule.tsx:105:4" data-component-path="src/app/care-maintenance/components/MaintenanceSchedule.tsx" data-component-line="105" data-component-end-line="170" data-component-file="MaintenanceSchedule.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22py-20%20lg%3Apy-28%20bg-background%22%7D" className="py-20 lg:py-28 bg-background">
      <div data-component-id="src/app/care-maintenance/components/MaintenanceSchedule.tsx:106:6" data-component-path="src/app/care-maintenance/components/MaintenanceSchedule.tsx" data-component-line="106" data-component-end-line="169" data-component-file="MaintenanceSchedule.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-6%20lg%3Apx-13%22%7D" className="max-w-7xl mx-auto px-6 lg:px-13">
        <div data-component-id="src/app/care-maintenance/components/MaintenanceSchedule.tsx:107:8" data-component-path="src/app/care-maintenance/components/MaintenanceSchedule.tsx" data-component-line="107" data-component-end-line="114" data-component-file="MaintenanceSchedule.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20mb-12%22%7D" className="text-center mb-12">
          <h2 data-component-id="src/app/care-maintenance/components/MaintenanceSchedule.tsx:108:10" data-component-path="src/app/care-maintenance/components/MaintenanceSchedule.tsx" data-component-line="108" data-component-end-line="110" data-component-file="MaintenanceSchedule.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22font-display%20text-4xl%20md%3Atext-5xl%20font-medium%20text-foreground%20mb-6%20tracking-tight%22%2C%22textContent%22%3A%22Maintenance%20Schedule%22%7D" className="font-display text-4xl md:text-5xl font-medium text-foreground mb-6 tracking-tight">
            Maintenance Schedule
          </h2>
          <p data-component-id="src/app/care-maintenance/components/MaintenanceSchedule.tsx:111:10" data-component-path="src/app/care-maintenance/components/MaintenanceSchedule.tsx" data-component-line="111" data-component-end-line="113" data-component-file="MaintenanceSchedule.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22font-body%20text-lg%20text-text-secondary%20max-w-2xl%20mx-auto%22%2C%22textContent%22%3A%22A%20structured%20approach%20to%20preserving%20your%20investment%20in%20timeless%20elegance%22%7D" className="font-body text-lg text-text-secondary max-w-2xl mx-auto">
            A structured approach to preserving your investment in timeless elegance
          </p>
        </div>

        <div data-component-id="src/app/care-maintenance/components/MaintenanceSchedule.tsx:116:8" data-component-path="src/app/care-maintenance/components/MaintenanceSchedule.tsx" data-component-line="116" data-component-end-line="135" data-component-file="MaintenanceSchedule.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20justify-center%20mb-12%22%7D" className="flex justify-center mb-12">
          <div data-component-id="src/app/care-maintenance/components/MaintenanceSchedule.tsx:117:10" data-component-path="src/app/care-maintenance/components/MaintenanceSchedule.tsx" data-component-line="117" data-component-end-line="134" data-component-file="MaintenanceSchedule.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22inline-flex%20bg-surface%20p-1%22%7D" className="inline-flex bg-surface p-1">
            <button data-component-id="src/app/care-maintenance/components/MaintenanceSchedule.tsx:118:12" data-component-path="src/app/care-maintenance/components/MaintenanceSchedule.tsx" data-component-line="118" data-component-end-line="125" data-component-file="MaintenanceSchedule.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22textContent%22%3A%22Routine%20Care%22%7D"
              onClick={() => setActiveTab('routine')}
              className={`px-8 py-3 font-cta text-sm tracking-wide transition-all duration-300 ${
                activeTab === 'routine' ?'bg-accent text-accent-foreground' :'text-foreground hover:text-accent'
              }`}
            >
              Routine Care
            </button>
            <button data-component-id="src/app/care-maintenance/components/MaintenanceSchedule.tsx:126:12" data-component-path="src/app/care-maintenance/components/MaintenanceSchedule.tsx" data-component-line="126" data-component-end-line="133" data-component-file="MaintenanceSchedule.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22textContent%22%3A%22Seasonal%20Care%22%7D"
              onClick={() => setActiveTab('seasonal')}
              className={`px-8 py-3 font-cta text-sm tracking-wide transition-all duration-300 ${
                activeTab === 'seasonal' ?'bg-accent text-accent-foreground' :'text-foreground hover:text-accent'
              }`}
            >
              Seasonal Care
            </button>
          </div>
        </div>

        <div data-component-id="src/app/care-maintenance/components/MaintenanceSchedule.tsx:137:8" data-component-path="src/app/care-maintenance/components/MaintenanceSchedule.tsx" data-component-line="137" data-component-end-line="168" data-component-file="MaintenanceSchedule.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20md%3Agrid-cols-2%20gap-8%22%7D" className="grid md:grid-cols-2 gap-8">
          {(activeTab === 'routine' ? routineTasks : seasonalTasks).map((schedule, index) => (
            <div data-component-id="src/app/care-maintenance/components/MaintenanceSchedule.tsx:139:12" data-component-path="src/app/care-maintenance/components/MaintenanceSchedule.tsx" data-component-line="139" data-component-end-line="166" data-component-file="MaintenanceSchedule.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-card%20p-8%20lg%3Ap-10%20transition-all%20duration-300%20hover%3Ashadow-subtle%22%7D"
              key={index}
              className="bg-card p-8 lg:p-10 transition-all duration-300 hover:shadow-subtle"
            >
              <div data-component-id="src/app/care-maintenance/components/MaintenanceSchedule.tsx:143:14" data-component-path="src/app/care-maintenance/components/MaintenanceSchedule.tsx" data-component-line="143" data-component-end-line="150" data-component-file="MaintenanceSchedule.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-6%22%7D" className="mb-6">
                <h3 data-component-id="src/app/care-maintenance/components/MaintenanceSchedule.tsx:144:16" data-component-path="src/app/care-maintenance/components/MaintenanceSchedule.tsx" data-component-line="144" data-component-end-line="146" data-component-file="MaintenanceSchedule.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22font-display%20text-2xl%20font-medium%20text-foreground%20mb-2%22%7D" className="font-display text-2xl font-medium text-foreground mb-2">
                  {schedule.frequency}
                </h3>
                {schedule.season && (
                  <p data-component-id="src/app/care-maintenance/components/MaintenanceSchedule.tsx:148:18" data-component-path="src/app/care-maintenance/components/MaintenanceSchedule.tsx" data-component-line="148" data-component-end-line="148" data-component-file="MaintenanceSchedule.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22font-body%20text-sm%20text-text-secondary%22%7D" className="font-body text-sm text-text-secondary">{schedule.season}</p>
                )}
              </div>

              <ul data-component-id="src/app/care-maintenance/components/MaintenanceSchedule.tsx:152:14" data-component-path="src/app/care-maintenance/components/MaintenanceSchedule.tsx" data-component-line="152" data-component-end-line="165" data-component-file="MaintenanceSchedule.tsx" data-component-name="ul" data-component-content="%7B%22elementName%22%3A%22ul%22%2C%22className%22%3A%22space-y-4%22%7D" className="space-y-4">
                {schedule.tasks.map((task, idx) => (
                  <li data-component-id="src/app/care-maintenance/components/MaintenanceSchedule.tsx:154:18" data-component-path="src/app/care-maintenance/components/MaintenanceSchedule.tsx" data-component-line="154" data-component-end-line="163" data-component-file="MaintenanceSchedule.tsx" data-component-name="li" data-component-content="%7B%22elementName%22%3A%22li%22%2C%22className%22%3A%22flex%20items-start%20space-x-3%22%7D" key={idx} className="flex items-start space-x-3">
                    <Icon data-component-id="src/app/care-maintenance/components/MaintenanceSchedule.tsx:155:20" data-component-path="src/app/care-maintenance/components/MaintenanceSchedule.tsx" data-component-line="155" data-component-end-line="159" data-component-file="MaintenanceSchedule.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ClockIcon%22%2C%22className%22%3A%22text-accent%20flex-shrink-0%20mt-0.5%22%7D"
                      name="ClockIcon"
                      size={20}
                      className="text-accent flex-shrink-0 mt-0.5"
                    />
                    <span data-component-id="src/app/care-maintenance/components/MaintenanceSchedule.tsx:160:20" data-component-path="src/app/care-maintenance/components/MaintenanceSchedule.tsx" data-component-line="160" data-component-end-line="162" data-component-file="MaintenanceSchedule.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-body%20text-sm%20text-foreground%20leading-relaxed%22%7D" className="font-body text-sm text-foreground leading-relaxed">
                      {task}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default MaintenanceSchedule;